/**
 * Configuración de Colores Dentales Estándar
 * 
 * Este archivo contiene la configuración de colores y tratamientos
 * utilizados en el odontograma. Fácil de modificar y mantener.
 */

// Colores estándar internacionales para odontograma
export const DENTAL_COLORS = [
  {
    id: 'red',
    name: 'Caries',
    description: 'Lesión cariosa activa',
    color: '#ef4444',           // Color del borde/texto
    bgColor: 'rgba(239, 68, 68, 0.6)',  // Color de fondo
    category: 'patologia',
    standard: 'ISO 3950'        // Referencia estándar
  },
  {
    id: 'blue',
    name: 'Tratamiento',
    description: 'Tratamiento en progreso',
    color: '#38b6ff',
    bgColor: 'rgba(56, 182, 255, 0.6)',
    category: 'tratamiento',
    standard: 'ISO 3950'
  },
  {
    id: 'green',
    name: 'Restauración',
    description: 'Obturación o restauración completada',
    color: '#10b981',
    bgColor: 'rgba(16, 185, 129, 0.6)',
    category: 'restauracion',
    standard: 'ISO 3950'
  },
  {
    id: 'orange',
    name: 'Endodoncia',
    description: 'Tratamiento de conducto',
    color: '#f59e0b',
    bgColor: 'rgba(245, 158, 11, 0.6)',
    category: 'endodoncia',
    standard: 'ISO 3950'
  },
  {
    id: 'purple',
    name: 'Extracción',
    description: 'Diente indicado para extracción',
    color: '#8b5cf6',
    bgColor: 'rgba(139, 92, 246, 0.6)',
    category: 'cirugia',
    standard: 'ISO 3950'
  },
  {
    id: 'pink',
    name: 'Prótesis',
    description: 'Corona, puente o prótesis',
    color: '#ec4899',
    bgColor: 'rgba(236, 72, 153, 0.6)',
    category: 'protesis',
    standard: 'ISO 3950'
  },
  {
    id: 'gray',
    name: 'Observación',
    description: 'Diente en observación o seguimiento',
    color: '#6b7280',
    bgColor: 'rgba(107, 114, 128, 0.6)',
    category: 'seguimiento',
    standard: 'Personalizado'
  },
  {
    id: 'yellow',
    name: 'Prevención',
    description: 'Sellador de fosas y fisuras',
    color: '#eab308',
    bgColor: 'rgba(234, 179, 8, 0.6)',
    category: 'prevencion',
    standard: 'ISO 3950'
  }
];

// Categorías de tratamientos para organización
export const TREATMENT_CATEGORIES = {
  patologia: {
    name: 'Patología',
    description: 'Enfermedades y lesiones dentales',
    icon: 'fas fa-exclamation-triangle'
  },
  tratamiento: {
    name: 'Tratamiento',
    description: 'Procedimientos en curso',
    icon: 'fas fa-tools'
  },
  restauracion: {
    name: 'Restauración',
    description: 'Obturaciones y restauraciones',
    icon: 'fas fa-fill-drip'
  },
  endodoncia: {
    name: 'Endodoncia',
    description: 'Tratamientos de conducto',
    icon: 'fas fa-syringe'
  },
  cirugia: {
    name: 'Cirugía',
    description: 'Procedimientos quirúrgicos',
    icon: 'fas fa-cut'
  },
  protesis: {
    name: 'Prótesis',
    description: 'Coronas, puentes y prótesis',
    icon: 'fas fa-crown'
  },
  seguimiento: {
    name: 'Seguimiento',
    description: 'Observación y controles',
    icon: 'fas fa-eye'
  },
  prevencion: {
    name: 'Prevención',
    description: 'Medidas preventivas',
    icon: 'fas fa-shield-alt'
  }
};

// Función para obtener un color por ID
export const getColorById = (colorId) => {
  return DENTAL_COLORS.find(color => color.id === colorId);
};

// Función para obtener colores por categoría
export const getColorsByCategory = (category) => {
  return DENTAL_COLORS.filter(color => color.category === category);
};

// Función para agregar un nuevo color/tratamiento
export const addCustomColor = (newColor) => {
  // Validar que tenga todos los campos requeridos
  const requiredFields = ['id', 'name', 'color', 'bgColor', 'category'];
  const hasAllFields = requiredFields.every(field => newColor[field]);
  
  if (!hasAllFields) {
    throw new Error('Faltan campos requeridos para el nuevo color');
  }
  
  // Verificar que el ID no exista
  const exists = DENTAL_COLORS.some(color => color.id === newColor.id);
  if (exists) {
    throw new Error('Ya existe un color con ese ID');
  }
  
  // Agregar el nuevo color
  DENTAL_COLORS.push({
    description: '',
    standard: 'Personalizado',
    ...newColor
  });
  
  return newColor;
};

// Función para generar leyenda de colores
export const generateColorLegend = () => {
  const legend = {};
  
  Object.keys(TREATMENT_CATEGORIES).forEach(categoryId => {
    const category = TREATMENT_CATEGORIES[categoryId];
    const colors = getColorsByCategory(categoryId);
    
    legend[categoryId] = {
      ...category,
      colors: colors
    };
  });
  
  return legend;
};

// Configuración por defecto exportada
const dentalConfig = {
  colors: DENTAL_COLORS,
  categories: TREATMENT_CATEGORIES,
  getColorById,
  getColorsByCategory,
  addCustomColor,
  generateColorLegend
};

export default dentalConfig;
